import {
  FilterValue,
  NestedFilter,
  IndexedFilterValue,
  IndexedNestedFilter,
  FilterRange,
} from '../types/filters';

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function isNumber(value: unknown): value is number {
  return typeof value === 'number';
}

function isArrayOfType<T>(
  input: unknown,
  typeCheck: (value: unknown) => value is T
): input is T[] {
  return Array.isArray(input) && input.every(typeCheck);
}

/**
 * Checks if the given value is a valid number range.
 */
export function isTypeNumberRange(
  filters: IndexedFilterValue
): filters is FilterRange<number> {
  return (
    typeof filters === 'object' &&
    filters !== null &&
    'min' in filters &&
    'max' in filters &&
    typeof filters.min === 'number' &&
    typeof filters.max === 'number'
  );
}

/**
 * Filters out undefined ranges to fallback on a default range of { min: -1, max: -1 } to represent an unselected range.
 * @param {IndexedFilterValue} filters
 * @returns The filtered string array.
 */
export function filterNumberRange(filters?: IndexedFilterValue): FilterRange<number> {
  if (filters && isTypeNumberRange(filters)) {
    return filters;
  }

  // In all our current cases, our number ranges are whole, positive numbers, so we can safely use -1 as a default value
  // to indicate that no range is selected.
  return { min: -1, max: -1 };
}

/**
 * Checks if the given value is a valid IndexedNestedFilter.
 */
export function isTypeIndexedNestedFilter(
  filters: IndexedFilterValue
): filters is IndexedNestedFilter {
  // Make sure filters is an object
  if (typeof filters !== 'object' || filters === null) return false;

  // Now we know that filters is an object, so we can cast it to Record<string, unknown>
  const obj = filters as Record<string, unknown>;

  // Make sure filters is not empty
  if (Object.keys(obj).length === 0) return false;

  // Make sure all keys are strings and all values are string arrays
  for (const key in obj) {
    if (!isString(key) || !isArrayOfType(obj[key], isString)) {
      return false;
    }
  }

  return true;
}

/**
 * Checks if the given value is a valid NestedFilter array.
 */
export function isTypeNestedFilterArray(value: FilterValue): value is NestedFilter[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    typeof value[0] === 'object' &&
    'name' in value[0]
  );
}

/**
 * Filters out non-string values from a IndexedNestedFilter.
 * @param filters
 * @returns
 */
export function filterIndexedNestedFilter(
  filters?: IndexedFilterValue
): IndexedNestedFilter {
  if (filters != null && typeof filters === 'object' && !Array.isArray(filters)) {
    const result: IndexedNestedFilter = {};

    for (const [key, value] of Object.entries(filters)) {
      if (Array.isArray(value)) {
        result[key] = value.filter(isString);
      }
    }

    return result;
  }

  return {};
}

/**
 * Checks if the given value is a valid string array.
 */
export function isTypeStringArray(filters: IndexedFilterValue): filters is string[] {
  return isArrayOfType<string>(filters, isString);
}

/**
 * Filters out non-string values from the given array.
 * @param {IndexedFilterValue} filters
 * @returns The filtered string array.
 */
export function filterStringArray(filters?: IndexedFilterValue): string[] {
  if (Array.isArray(filters)) {
    return filters.filter(isString);
  }
  return [];
}

/**
 * Checks if the given value is a valid number array.
 */
export function isTypeNumberArray(filters: IndexedFilterValue): filters is number[] {
  return isArrayOfType<number>(filters, isNumber);
}

/**
 * Filters out non-number values from the given array.
 * @param {IndexedFilterValue} filters
 * @returns The filtered number array.
 */
export function filterNumberArray(filters?: IndexedFilterValue): number[] {
  if (Array.isArray(filters)) {
    return filters.filter(isNumber);
  }
  return [];
}
